import React, { useState } from 'react';
import axios from 'axios';
import { DataGrid, LoadPanel } from 'devextreme-react';
import { Column, Toolbar, Item } from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import EditPayrollSalaryPopup from './EditPayrollSalaryPopup';

const API_BASE = "https://qbo.omnivistaweb.com";

interface SalaryDetail {
  pay_detail_id: number;
  item_id: string;
  amount: number;
  notes?: string;
}

interface SalaryRow {
  employee_id: string;
  start_date: string;
  pay_type: string;
  pay_detail_id: number;
  details: SalaryDetail[];
}

const Payroll = () => {
  const [latestSalaries, setLatestSalaries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);

  // Fetch latest salary data
  const fetchLatestSalaries = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/employees/salaryLatest`);
      setLatestSalaries(response.data || []);
    } catch (error) {
      console.error('Failed to fetch latest salaries:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchLatestSalaries();
  }, []);

  const handleAdd = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/employees/pay-detail-id/next`);
      const newRow: SalaryRow = {
        employee_id: '',
        start_date: '',
        pay_type: '',
        pay_detail_id: response.data.next, // Fetch the next ID for the new row
        details: [], // Initialize with an empty details array
      };
      setSelectedRowData(newRow);
      setPopupVisible(true);
    } catch (error) {
      console.error('Failed to fetch next pay_detail_id:', error);
    }
  };

  const handleEdit = (rowData: any) => {
    setSelectedRowData(rowData); // Pass the selected row data to the popup
    setPopupVisible(true); // Show the popup
  };

  const handleDelete = async (rowData: any) => {
    try {
      await axios.post(`${API_BASE}/payroll/salary/delete`, rowData);
      setLatestSalaries((prev) => prev.filter((row) => row !== rowData));
      console.log('Row deleted successfully');
    } catch (error) {
      console.error('Failed to delete row:', error);
    }
  };

  const handlePopupClose = (updatedData: any) => {
    setPopupVisible(false); // Hide the popup
    if (updatedData) {
      // Update the grid data if changes were made
      if (selectedRowData) {
        // Edit existing row
        setLatestSalaries((prev) =>
          prev.map((row) => (row === selectedRowData ? updatedData : row))
        );
      } else {
        // Add new row
        setLatestSalaries((prev) => [...prev, updatedData]);
      }
    }
  };

  return (
    <div className="dashboard-wrapper" style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: '0 0 auto', position: 'sticky', top: 0, zIndex: 1000 }}>
        <div style={{ background: 'transparent', padding: '16px 16px 0' }}>
          <div className="dashboard-controls" style={{ background: '#fff', padding: 16, borderRadius: 8 }}>
            <h3>Run Pay Cycle</h3>
            {loading && <LoadPanel visible={loading} />}
            <DataGrid
              dataSource={latestSalaries}
              showBorders
              columnAutoWidth
              style={{ marginTop: 16 }}
              onRowDblClick={(e) => handleEdit(e.data)}
            >
              <Column dataField="employee_id" caption="Employee ID" />
              <Column dataField="start_date" caption="Start Date" dataType="date" />
              <Column dataField="pay_type" caption="Pay Type" />
              <Column dataField="pay_detail_id" caption="Pay Detail ID" />
              <Column
                type="buttons"
                caption="Actions"
                buttons={[
                  {
                    icon: 'edit', // Use the same icon as EmployeeView
                    onClick: (e) => handleEdit(e.row?.data),
                  },
                  {
                    icon: 'trash', // Use the same icon as EmployeeView
                    onClick: (e) => handleDelete(e.row?.data),
                  },
                ]}
              />
              <Toolbar>
                <Item location="before">
                  <Button
                    icon="add" // Use the same icon as EmployeeView
                    text="Add"
                    type="default"
                    stylingMode="contained"
                    onClick={handleAdd}
                  />
                </Item>
              </Toolbar>
            </DataGrid>
          </div>
        </div>
      </div>

      {/* Popup for Adding/Editing Salary */}
      {popupVisible && (
        <EditPayrollSalaryPopup
          data={selectedRowData}
          onClose={handlePopupClose}
        />
      )}
    </div>
  );
};

export default Payroll;